export const en = {
  app: {
    name: 'BN-games',
    tagline: 'Classic board games in your browser. Free. Nothing to install, no account needed.',
  },
  seo: {
    title: 'Free Online Board Games — Chess, Backgammon (Nardi), Checkers, Dominoes, Mahjong & Sudoku | BN-games',
    description: 'Classic board games in your browser — Chess, Long Backgammon, Checkers, Dominoes, Sudoku, and Mahjong. Free. Nothing to install, no account needed.',
    social: 'Classic board games in your browser — Chess, Long Backgammon, Checkers, Dominoes, Sudoku, and Mahjong.',
  },
  notFound: {
    title: 'Page not found — BN-games',
    heading: 'Page not found',
    body: 'This page doesn\'t exist or has moved. Pick a game below, or go to the home page.',
    home: 'Go to the home page',
  },
  common: {
    play: 'Play',
  },
  auth: {
    signIn: 'Sign in',
    signOut: 'Sign out',
    google: 'Google',
    github: 'GitHub',
    completeProfile: 'Complete your profile →',
    profile: 'Profile',
  },
  nav: {
    games: 'Games',
    about: 'About',
    changelog: 'Changelog',
  },
  hero: {
    title: 'Classic board games, right in your browser',
    subtitle:
      'Play someone next to you, play someone across the world, or play a bot. Just open the page — nothing to install, no account needed.',
    social: 'Add friends, see who\'s online, and invite them straight into a game.',
  },
  games: {
    sectionTitle: 'Games',
    rulesLink: 'How to play',
    backgammon: {
      name: 'Long Backgammon',
      description:
        'Nardi — the long-track version played across the Caucasus and Balkans. Same device, online, or against a bot.',
      cta: 'Play Long Backgammon',
      thumbAlt: 'Long Backgammon gameplay screenshot',
    },
    checkers: {
      name: 'Checkers',
      description:
        'Two variants — Soviet Shashki (flying kings, mid-jump promotion) and English Draughts. Same device, online, or against a bot with four difficulty levels.',
      cta: 'Play Checkers',
      thumbAlt: 'Checkers gameplay screenshot',
    },
    domino: {
      name: 'Dominoes',
      description:
        'Three games — Kozyol (the Russian "goat"), Telephone and All Fives (Muggins). 2–4 players, each on their own or as pairs. Same device, online, or against a bot with three difficulty levels.',
      cta: 'Play Dominoes',
      thumbAlt: 'Dominoes — early hand with the chain branching off the spinner',
    },
    chess: {
      name: 'Chess',
      newBadge: 'New',
      description:
        'Standard chess by FIDE rules. Play a bot with four levels, a friend on the same device, or anyone online with a room code — with or without a clock. Every game is saved to replay move by move, and exports to PGN.',
      cta: 'Play Chess',
      thumbAlt: 'Chess — the starting position on a wooden board',
    },
    mahjong: {
      name: 'Mahjong',
      description:
        'Learn the game, then play it for real. Six guided lessons and a coach that explains every suggested discard, a simplified teaching variant and full Chinese Classical with kong and scoring, and online tables for up to four players — bots fill any seat nobody takes.',
      cta: 'Play Mahjong',
      thumbAlt: 'Mahjong — a four-seat table mid-hand, tiles laid out around a central discard pond',
    },
    sudoku: {
      name: 'Sudoku',
      description:
        'One-tap to start. Phone-friendly down to the smallest screen, with a fresh, unique puzzle every game. Three difficulty tiers, pencil marks, hints that name the technique, and a shareable link if you find a great one.',
      cta: 'Play Sudoku',
      thumbAlt: 'Sudoku — a 9×9 grid mid-solve with pencil marks',
    },
  },
  about: {
    sectionTitle: 'About',
    body: 'A small collection of classic board games for the browser. Everything runs on your device — open a page and play. Online rooms use one shared server; nothing else leaves the page. Signing in is optional: it gives you a name other players see, a friends list that shows who\'s online, invites that reach friends on any page, and your game history on every device. No tracking, no ads. Free, and meant to stay that way.',
  },
  changelog: {
    sectionTitle: 'Changelog',
    showAll: 'Full changelog →',
    entries: {
      chessLaunch:
        'Chess is here — standard FIDE rules against a bot with four levels, with a friend on one device, or online with a room code, with an optional Bullet, Blitz or Rapid clock and draw offers. Every game is saved: replay it move by move, export it as PGN or FEN, or paste a position and play on from it. Signed in, your online games sync to your account. Also fixed: a Dominoes or Mahjong room could close an hour into a game that was still being played.',
      kozyolAndOnlineFixes:
        'Dominoes now has the real Kozyol — the Russian courtyard game played in a line, where the loser writes down their pips and whoever reaches 101 is the goat — with bots, online play and replays. The game we used to call Kozyol is now Telephone, its proper name. Online, players rejoining a room before it starts no longer see a blank screen, Muggins auto-claim is on by default, and the "add to friends" offer skips players who already are.',
      publicLaunch:
        'Version 1.0 — BN-games is officially public. Every game now has its own page in Russian and a how-to-play page in both languages, this page loads faster with lighter screenshots, and the game menus no longer cut off their last button.',
      invitesEverywhere:
        'Game invites now arrive instantly on any page — in the menus, on this site, and as a small badge while you play — and your friends list shows you online for as long as any of your tabs is open.',
      inviteAndPresenceFixes:
        'Accepting a game invite now takes you straight into the room without reloading the page, and friends who are in the middle of a game show as "in a game" instead of offline.',
      realtimeOnCloudflare:
        'Online games moved to a new realtime server: a match in progress now survives server updates instead of ending, rooms wait for everyone to reconnect, and friends can see when you were last online.',
      signedInPlayerSurfaces:
        'Your profile page now links straight to Friends, opponents see the display name you picked (not whatever your sign-in provider called you), and your online match history for Long Backgammon, Checkers, and Dominoes lives under "Your account" — safely synced, not just sitting in this browser.',
      accountsAndIdentities:
        'Account management: link Google, GitHub, and Discord to one account, see every device you\'re signed into, and delete your account entirely if you want to.',
      roomCodesSixChar:
        'Online room codes are now six characters instead of four, generated more securely.',
      resumeParity:
        'Checkers, Dominoes and Mahjong now resume an online match the same reliable way Long Backgammon always has — reopening your own room link no longer tells you the room is full, and Mahjong\'s invite links actually work now.',
      onlineIdentityFixes:
        'Fixed two bugs quietly breaking online identity: taking more than a minute to start an online match could seat you anonymously even while signed in, and some signed-in matches weren\'t being recorded to your history at all.',
      serverHistorySync:
        'Signed-in players\' online match history now syncs to your account — see your win/loss record and replay past matches from any device in Long Backgammon, Checkers, and Dominoes.',
      friendsAndInvites:
        'Friends and invites. Add friends by their handle, see who\'s online, and invite them straight into a room — with live presence and a push notification the moment an invite arrives, across all four online games.',
      dominoConnectionOverlay:
        'Dominoes now tells you when a connection hiccup is why tile dragging has frozen, instead of leaving you guessing.',
      onlineReconnectFix:
        'Online rooms are more reliable: a shared room link or a stored session now gets you straight back into your seat in one step, and a dropped connection no longer wipes out sessions for rooms you weren\'t even in.',
      landingHeaderNav:
        'Every game\'s main menu now has a sign-in/sign-out control and a link back to the games list, matching the one on the landing page.',
      authPolishLegalPages:
        'Fixed a broken "Complete your profile" link in Long Backgammon and added a missing sign-out button. Privacy Policy and Terms of Service pages are now live and linked from the footer.',
      identityInRooms:
        'Long Backgammon shows your real name and avatar to your opponent in online rooms when you\'re signed in — verified server-side, so nobody can fake somebody else\'s name.',
      mahjongLaunch:
        'Mahjong launched — learn it, then play it for real. Six guided lessons and a coach that explains every discard it suggests, a simplified teaching variant plus full Chinese Classical with kong and traditional scoring, and online tables for up to four players where bots fill any seat nobody takes. Your tiles are never sent to the other players, the wall is dealt from the same public randomness beacon the dice use, and Escape pauses the game at any time.',
      optionalSignIn:
        'Optional sign-in with Google or GitHub. Pick a handle and a display name once, and online opponents see you by name instead of "Player". Entirely optional — every game plays exactly the same without an account.',
      dominoLastAction:
        'Dominoes now tells you what just happened — who played which tile and what they scored, who drew, who passed — before saying whose turn it is, and your view of the board no longer jumps when an opponent moves. In Telephone the lead rotates clockwise each hand, and the rules screen explains it. On the Join screen in Long Backgammon, Checkers and Dominoes you can now paste a room code with Ctrl+V.',
      afkPing:
        'Nudge an opponent who has gone quiet. In online games a bell appears once it has been their turn for a while — press it and a chime plays on their side. Both sending and receiving can be switched off in settings.',
      sudokuLaunch:
        'Sudoku launched — classic 9×9, solo. Three difficulty tiers powered by a logical solver, pencil marks, hints that name the technique you needed, mm:ss stopwatch, resume in-progress, and a shareable link to send a puzzle to a friend.',
      dominoBoneyardPolish:
        'Dominoes: click the boneyard pile to draw — the separate Draw button is gone. Hover a tile in your hand to preview where it can go. Optional setting to show the boneyard count.',
      pwaInstall:
        'Every game is now installable. Add it to your home screen or desktop and it runs in its own window with its own icon — no browser tabs, no toolbar.',
      verifiableRng:
        'Provably-fair dice rolls and shuffles. Verifiable rooms seed every roll and deal from a public randomness beacon (drand), and a verifier in the history lets you — or anyone — confirm a past game was honest.',
      accessibilityPass:
        'Full accessibility pass. Every game is playable from the keyboard, a screen reader narrates each move, all four themes meet WCAG AA contrast, and reduced-motion preferences are respected throughout.',
      inGameOverlay:
        'In-game pause menu with Restart, Leave, Give up, and a tabbed Settings panel — reachable any time with Escape or the gear icon. If you go quiet on your turn, a soft chime and a glowing legal move help you find your way back. New one-tap Quick Start on the main menu.',
      landingRefresh:
        'Landing page redesigned with plain-language copy, per-language URLs (English at /, Russian at /ru/), social preview cards, and a language switcher in the footer.',
      menuOverhaul:
        'Menus polished across every game. Online rooms now have copy-to-clipboard codes and shareable join links, paste-the-link works on the Join screen, and the layout scales cleanly on big displays and mobile.',
      backgammonOnlineFixes:
        'Long Backgammon online: fixed stale games hanging around after a reload, fixed the side swap on rematch, and moved the Expert bot search off the main thread for a smoother game.',
      dominoLaunch:
        'Dominoes launched with two variants — All Fives and Telephone. 2–4 players, each on their own or as pairs. Same-device, online, and three difficulty levels of bot opponent. Full replay history.',
      monorepoMigration: 'Long Backgammon moved into the BN-games project alongside Checkers.',
      checkersLaunch:
        'Checkers launched — Shashki rules: same-device, online, and four difficulty levels of bot opponent.',
      backgammonLaunch:
        'Long Backgammon launched — same-device, online, and against a bot. Full replay history.',
    },
  },
  footer: {
    yearLine: '© {{year}} BN-games',
    legalNav: 'Legal',
    privacy: 'Privacy Policy',
    terms: 'Terms of Service',
  },
  language: {
    toggleLabel: 'Language',
    en: 'English',
    ru: 'Русский',
  },
  landing: {
    skipLink: 'Skip to main content',
  },
  // Only the keys `@bn-games/ui-shell/social`'s invite dock reads.
  social: {
    invite: {
      message: '{{sender}} invites you to {{game}}',
      moreCount_one: '+{{count}} more invite',
      moreCount_few: '+{{count}} more invites',
      moreCount_many: '+{{count}} more invites',
      moreCount_other: '+{{count}} more invites',
      accept: 'Accept',
      decline: 'Decline',
      acceptAria: "Accept {{sender}}'s {{game}} invite",
      declineAria: "Decline {{sender}}'s {{game}} invite",
    },
    dock: {
      pillLabel: 'From {{sender}}',
      pillAria: 'Invite from {{sender}} to {{game}} — show',
      popoverLabel: 'Invite',
      close: 'Close invite',
      leaveGameTitle: 'Leave this game?',
      leaveGameBody: 'Accepting opens {{game}} and leaves the current game. In an online game your seat is held for a while, so you can come back.',
      leaveGameConfirm: 'Leave and join',
      leaveGameCancel: 'Stay',
    },
  },
};
